/**
 * Knex implementation of IViewRepository.
 *
 * Read-only adapter for SQL views (or any table used as read-only).
 * No write methods — pure projection layer.
 */

import type { Knex } from 'knex'
import type { EntityQuery, FieldDefinition, PaginatedResult } from '@gzl10/nexus-sdk'
import type { IViewRepository, RepoFilters } from '../types.js'
import {
  applyFilters,
  applySorting,
  applyPagination,
  applySearchOnFields,
  type SortOptions
} from './helpers.js'

export interface KnexViewRepoOptions {
  searchableFields?: string[]
  defaultSort?: { field: string; order: 'asc' | 'desc' } | null
  labelField?: string
  fieldDefs?: Record<string, FieldDefinition>
  /** Maximum page size cap. Defaults to 100. */
  maxLimit?: number
}

export class KnexViewRepository<T extends Record<string, unknown> = Record<string, unknown>>
  implements IViewRepository<T>
{
  constructor(
    protected readonly knex: Knex,
    protected readonly view: string,
    protected readonly options: KnexViewRepoOptions = {}
  ) {}

  async findAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    const page = Math.max(1, query?.page ?? 1)
    const maxLimit = this.options.maxLimit ?? 100
    const limit = query?.limit === 0 ? 0 : Math.min(maxLimit, Math.max(1, query?.limit ?? 20))
    const offset = limit === 0 ? 0 : (page - 1) * limit

    let qb: Knex.QueryBuilder = this.knex(this.view)

    // Apply search
    if (query?.search) {
      const { searchableFields, labelField, fieldDefs } = this.options
      const fields = [
        ...(searchableFields ?? []),
        ...(labelField && !searchableFields?.includes(labelField) ? [labelField] : [])
      ]
      if (fields.length > 0) {
        qb = applySearchOnFields(qb, query.search, fields, fieldDefs)
      }
    }

    // Apply filters
    if (query?.filters) {
      qb = applyFilters(qb.clone(), query.filters)
    }

    // Count before adding sorting/pagination
    const total = Number(
      (await qb.clone().count('* as count').first<{ count: string | number }>())?.count ?? 0
    )

    const sortOpts: SortOptions = {
      defaultSort: this.options.defaultSort ?? undefined,
      labelField: this.options.labelField
    }

    applySorting(qb, query, sortOpts)
    applyPagination(qb, limit, offset)

    const items = (await qb) as T[]

    const totalPages = limit === 0 ? 1 : Math.ceil(total / limit)

    return {
      items,
      total,
      page: limit === 0 ? 1 : page,
      limit: limit === 0 ? total : limit,
      totalPages,
      hasNext: limit === 0 ? false : page < totalPages
    }
  }

  async findById(id: string): Promise<T | null> {
    const row = await this.knex(this.view).where('id', id).first<T | undefined>()
    return row ?? null
  }

  async count(filters?: RepoFilters): Promise<number> {
    let qb: Knex.QueryBuilder = this.knex(this.view)
    if (filters) {
      qb = applyFilters(qb, filters)
    }
    const result = await qb.count('* as count').first<{ count: string | number }>()
    return Number(result?.count ?? 0)
  }
}
